import CheckIcon from '@mui/icons-material/CheckCircleOutline'
import EditIcon from '@mui/icons-material/Edit'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  DataGrid,
  GridCellModes,
  GridCellParams,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridSortModel,
} from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import { FC, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { RecoveryCodeType, UserInfoType, UserType } from '../../api/appAPI'
import {
  copyTextToClipboard,
  encryptAES,
  saveGridStateInLocalStorage,
  setCharAt,
} from '../../common/helpers'
import { ConfigsType } from '../../config'
import {
  RoleType,
  actions as appActions,
  editCompanyOfferMarkups,
  editCompanyOptions,
  generateRecoveryCode,
  setUserField,
  setUserRole,
} from '../../redux/appReducer'
import { StateType } from '../../redux/store'
import { EnqueueSnackbarType, Flex, USPhoneInput } from '../common/Helpers'
import OfferMarkup from '../common/OfferMarkup'
import PasswordIcon from '@mui/icons-material/Password'
import LinkIcon from '@mui/icons-material/Link'
import QuestionIcon from '@mui/icons-material/HelpOutline'

const RoleCellEditComponent: FC<{
  myRole: RoleType
  userRole: RoleType

  submitChange(role: RoleType): void
}> = ({ userRole, myRole, submitChange }) => {
  const [role, setRole] = useState(userRole)

  return myRole === 'company' || myRole === 'admin' ? (
    <Box display="flex" width={1} alignItems="center">
      <FormControl style={{ width: '100%' }} variant="outlined" size="small">
        <Select
          value={role}
          onChange={(e) => {
            setRole(e.target.value as RoleType)
          }}
          displayEmpty
        >
          <MenuItem value="none">None</MenuItem>
          <MenuItem value="designer">Designer</MenuItem>
          <MenuItem value="designer2">Designer 2</MenuItem>
          <MenuItem value="engineer">Engineer</MenuItem>
          {myRole === 'admin' && <MenuItem value="dealer">Dealer</MenuItem>}
          {myRole === 'admin' && <MenuItem value="company">Company</MenuItem>}
          {myRole === 'admin' && <MenuItem value="admin">Admin</MenuItem>}
        </Select>
      </FormControl>
      <IconButton
        onClick={() => {
          submitChange(role)
        }}
      >
        <CheckIcon style={{ color: '#4caf50' }} />
      </IconButton>
    </Box>
  ) : (
    <></>
  )
}

const RoleCellComponent: FC<{
  role: RoleType

  onEditClick(): void
}> = ({ role, onEditClick }) => {
  return (
    <Box
      display="flex"
      width={1}
      alignItems="center"
      justifyContent="space-between"
    >
      <Typography style={{ color: role === 'none' ? 'red' : '' }}>
        {role}
      </Typography>
      <IconButton
        onClick={() => {
          onEditClick()
        }}
      >
        <EditIcon />
      </IconButton>
    </Box>
  )
}

const EditableCellEditComponent: FC<{
  myRole: RoleType
  startValue: string
  isNumber?: boolean

  submitChange(v: string): void
}> = (props) => {
  const [value, setValue] = useState(props.startValue)

  return props.myRole === 'admin' || props.myRole === 'company' ? (
    <Box display="flex" width={1} alignItems="center">
      {props.isNumber ? (
        <USPhoneInput
          value={value || ''}
          small
          withoutLabel
          onChange={setValue}
        />
      ) : (
        <TextField
          size="small"
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
          }}
        />
      )}
      <IconButton
        onClick={() => {
          props.submitChange(value)
        }}
      >
        <CheckIcon style={{ color: '#4caf50' }} />
      </IconButton>
    </Box>
  ) : (
    <></>
  )
}

const EditableCellComponent: FC<{
  myRole: RoleType
  value: string

  onEditClick(): void
}> = (props) => {
  return (
    <Box
      display="flex"
      width={1}
      alignItems="center"
      justifyContent="space-between"
    >
      <Typography
        sx={{
          maxWidth: 0.7,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
      >
        {props.value}
      </Typography>

      {(props.myRole === 'admin' || props.myRole === 'company') && (
        <IconButton
          onClick={() => {
            props.onEditClick()
          }}
        >
          <EditIcon />
        </IconButton>
      )}
    </Box>
  )
}

interface ICompanyOptionsProps {
  open: boolean
  selectedCompany: UserInfoType | undefined
  companyOptions: Object
  allDistributorCompanies: string[]
  fuelSurchargeFee: number

  close(): void
  editCompanyOptions(options: Object): void
}

const roles = [
  'Company/Owner',
  'Buyer/Engineer',
  'Retail',
  'Dealer Costumer',
  'Designer',
  'Designer 2',
]

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

const CompanyOptionsDialog: FC<ICompanyOptionsProps> = (props) => {
  const [options, setOptions] = useState<any>(props.companyOptions) //object
  const [distributorCompanies, setDistributorCompanies] = useState<string[]>(
    // @ts-ignore
    props.companyOptions.distributorCompanies
      ? // @ts-ignore
        props.companyOptions.distributorCompanies.split(',')
      : []
  )

  const handleChange = (
    event: SelectChangeEvent<typeof distributorCompanies>
  ) => {
    const {
      target: { value },
    } = event
    setDistributorCompanies(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value
    )
  }

  useEffect(() => {
    setOptions(props.companyOptions)
    setDistributorCompanies(
      // @ts-ignore
      props.companyOptions.distributorCompanies
        ? // @ts-ignore
          props.companyOptions.distributorCompanies.split(',')
        : []
    )
  }, [props.companyOptions])

  useEffect(() => {
    options.distributorCompanies = distributorCompanies.join(',')
    setOptions({ ...options })
  }, [distributorCompanies])

  if (!props.companyOptions) return <></>

  const handleSaveClick = () => {
    props.editCompanyOptions(options)
  }

  return (
    <Dialog open={props.open} onClose={props.close} fullWidth maxWidth={'lg'}>
      <DialogTitle>Company {props.selectedCompany?.email} Options</DialogTitle>
      <DialogContent>
        <Flex centerX column>
          <Flex sx={{ my: 2, gap: 2 }}>
            <FormControl variant="outlined">
              <InputLabel>Freight</InputLabel>
              <OutlinedInput
                label="Freight"
                startAdornment={
                  <InputAdornment position="start">$</InputAdornment>
                }
                type="number"
                value={options.freight || 0}
                onChange={(e) => {
                  options.freight = e.target.value

                  setOptions({ ...options })
                }}
              />
            </FormControl>
            <FormControl sx={{ maxWidth: 250 }} variant="outlined">
              <InputLabel>Freight Price Per Pound</InputLabel>
              <OutlinedInput
                label="Freight Price Per Pound"
                startAdornment={
                  <InputAdornment position="start">$</InputAdornment>
                }
                endAdornment={
                  <InputAdornment position="end">
                    =
                    {' ' +
                      parseFloat(
                        (
                          (Number(options.freightPPP) || 0) *
                          props.fuelSurchargeFee
                        ).toFixed(3)
                      )}{' '}
                    <Tooltip
                      title={`Fuel Surcharge Markup: ${props.fuelSurchargeFee}`}
                    >
                      <QuestionIcon sx={{ ml: 1 }} />
                    </Tooltip>
                  </InputAdornment>
                }
                type="number"
                value={options.freightPPP || 0}
                onChange={(e) => {
                  options.freightPPP = e.target.value

                  setOptions({ ...options })
                }}
              />
            </FormControl>
            <FormControl variant="outlined">
              <InputLabel>Credit Card Charge</InputLabel>
              <OutlinedInput
                label="Credit Card Charge"
                startAdornment={
                  <InputAdornment position="start">%</InputAdornment>
                }
                type="number"
                value={options.credit || 0}
                onChange={(e) => {
                  options.credit = e.target.value

                  setOptions({ ...options })
                }}
              />
            </FormControl>

            <FormControl sx={{ width: 300 }}>
              <InputLabel id="demo-multiple-checkbox-label">
                Floor Distributor Companies
              </InputLabel>
              <Select
                labelId="demo-multiple-checkbox-label"
                id="demo-multiple-checkbox"
                multiple
                value={distributorCompanies}
                onChange={handleChange}
                input={<OutlinedInput label="Floor Distributor Companies" />}
                renderValue={(selected) => selected.join(',')}
                MenuProps={MenuProps}
              >
                {props.allDistributorCompanies.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Checkbox
                      checked={distributorCompanies.indexOf(name) > -1}
                    />
                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Flex>

          <Box sx={{ border: 1, borderColor: 'divider' }}>
            <Flex>
              {['', ...roles].map((r) => (
                <Flex
                  centerX
                  centerY
                  sx={{
                    height: 50,
                    minWidth: 150,
                    borderRight: 1,
                    borderColor: 'divider',
                    '&:last-child': {
                      borderRight: 0,
                    },
                  }}
                >
                  <Typography>{r}</Typography>
                </Flex>
              ))}
            </Flex>

            {Object.keys(options).map((key) =>
              [
                'freight',
                'freightPPP',
                'credit',
                'discount',
                'customerStatuses',
                'distributorCompanies',
              ].includes(key) ? (
                <></>
              ) : (
                <Flex key={key}>
                  {/* @ts-ignore */}
                  {[key, ...options[key].split('').map((o) => o === '1')].map(
                    (val, i) => (
                      <Flex
                        key={i}
                        centerX
                        centerY
                        sx={{
                          height: 50,
                          minWidth: 150,
                          borderRight: 1,
                          borderTop: 1,
                          borderColor: 'divider',
                          '&:last-child': {
                            borderRight: 0,
                          },
                        }}
                      >
                        {typeof val === 'string' ? (
                          <Typography>{val}</Typography>
                        ) : (
                          <Checkbox
                            checked={val}
                            onChange={(e, value) => {
                              const newOptions = { ...options }
                              // @ts-ignore
                              newOptions[key] = setCharAt(
                                newOptions[key],
                                i - 1,
                                value ? '1' : '0'
                              )

                              setOptions(newOptions)
                            }}
                          />
                        )}
                      </Flex>
                    )
                  )}
                </Flex>
              )
            )}
          </Box>
        </Flex>
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button
          size="medium"
          color="primary"
          variant="contained"
          onClick={handleSaveClick}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface IProps {
  configs: ConfigsType
  user: UserType | undefined
  usersInfo: UserInfoType[]
  recoveryCodes: RecoveryCodeType[]
  usersInfoGridVisibilityModel: GridColumnVisibilityModel | undefined
  usersInfoGridSortModel: GridSortModel | undefined
  usersInfoGridFilterModel: GridFilterModel | undefined

  setUserRole(
    email: string,
    role: RoleType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  setUserField(
    email: string,
    fieldName: string,
    fieldValue: string,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  setUsersInfoGridVisibilityModel(
    model: GridColumnVisibilityModel | undefined
  ): void
  setUsersInfoGridSortModel(model: GridSortModel | undefined): void
  setUsersInfoGridFilterModel(model: GridFilterModel | undefined): void
  editCompanyOfferMarkups(
    companyEmail: string,
    offerMarkups: number[],
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  editCompanyOptions(
    companyEmail: string,
    options: string,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
  generateRecoveryCode(
    userEmail: string,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
}

const UsersInfoTab: FC<IProps> = (props) => {
  const [editRowsModel, setEditRowsModel] = useState({})
  const [offerMarkupsEmail, setOfferMarkupsEmail] = useState<
    undefined | string
  >(undefined)
  const [selectedCompany, setSelectedCompany] = useState<
    UserInfoType | undefined
  >(undefined)
  const [optionsEmail, setOptionsEmail] = useState<undefined | string>(
    undefined
  )
  const [offerMarkups, setOfferMarkups] = useState<number[]>([])
  const [selectedCompanyOptions, setSelectedCompanyOptions] = useState<Object>(
    {}
  )
  const [recoverPasswordEmail, setRecoverPasswordEmail] = useState('')
  const [actualRecoveryCode, setActualRecoveryCode] = useState('')

  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    const company = props.usersInfo.find(
      (user) => user.email === offerMarkupsEmail
    )
    setSelectedCompany(company)

    if (company) {
      const _offerMarkups = [...company.offerMarkups]
      const _offerMarkupsLength = _offerMarkups.length
      if (_offerMarkupsLength < 14) {
        for (let i = 0; i < 14 - _offerMarkupsLength; i++) _offerMarkups.push(0)
      }
      setOfferMarkups(_offerMarkups)
    } else {
      setOfferMarkups([])
    }
  }, [offerMarkupsEmail])

  useEffect(() => {
    const company = props.usersInfo.find((user) => user.email === optionsEmail)
    setSelectedCompany(company)

    if (company && optionsEmail) {
      const optionKeys = props.configs.companyOptionNames.strValues
        .split(',')
        .map((v) => v.trim())

      if (company.options) {
        try {
          const options = JSON.parse(company.options)

          for (const key of optionKeys.filter(
            (k) => !Object.keys(options).includes(k)
          )) {
            //left only keys which options doesn't have
            // @ts-ignore
            options[key] = '000000'
          }
          setSelectedCompanyOptions(options)
        } catch (error) {
          alert('Not valid options string ' + company.options)
          alert('Options will be generated anew')

          const options = {}
          for (const key of optionKeys) {
            // @ts-ignore
            options[key] = '000000'
          }
          setSelectedCompanyOptions(options)
        }

        // setSelectedCompanyOptions({
        //     optBuyButton: company.optBuyButton.split('').map(o => o === '1')
        // })
      } else {
        const options = {}
        for (const key of optionKeys) {
          // @ts-ignore
          options[key] = '000000'
        }
        setSelectedCompanyOptions(options)
      }
    } else {
      setOptionsEmail(undefined)
      // setSelectedCompanyOptions(undefined)
    }
  }, [optionsEmail])

  useEffect(() => {
    setActualRecoveryCode(
      props.recoveryCodes.find((c) => c.userEmail === recoverPasswordEmail)
        ?.code || ''
    )
  }, [recoverPasswordEmail, props.recoveryCodes])

  useEffect(() => {
    saveGridStateInLocalStorage(
      {
        visibilityModel: props.usersInfoGridVisibilityModel,
        filterModel: props.usersInfoGridFilterModel,
        sortModel: props.usersInfoGridSortModel,
      },
      'usersInfo'
    )
  }, [
    props.usersInfoGridVisibilityModel,
    props.usersInfoGridFilterModel,
    props.usersInfoGridSortModel,
  ])

  if (!props.user) return <></>

  const myRole = props.user.role

  const handleSaveClick = () => {
    if (selectedCompany)
      props.editCompanyOfferMarkups(
        selectedCompany.email,
        offerMarkups,
        enqueueSnackbar
      )
  }

  // const handleOptionClick = () => {
  //     if (selectedCompany)
  //         props.editCompanyOfferMarkups(selectedCompany.email, offerMarkups, enqueueSnackbar)
  // }

  return (
    <Box style={{ height: window.innerHeight - 48 * 2 }}>
      <DataGrid
        style={{ width: '100%', maxHeight: '90%', overflowY: 'auto' }}
        columns={(() => {
          const columns: any[] = [
            { field: 'id', type: 'number' },
            { field: 'email', headerName: 'Email', width: 200 },
            {
              field: 'name',
              headerName: 'First Name',
              width: 200,
              editable: true,
              renderCell: (params: GridRenderCellParams) => (
                <EditableCellComponent
                  value={params.value as string}
                  myRole={myRole}
                  onEditClick={() => {
                    setEditRowsModel({
                      [params.id]: { name: { mode: GridCellModes.Edit } },
                    })
                  }}
                />
              ),
              renderEditCell: (params: GridRenderEditCellParams) => (
                <EditableCellEditComponent
                  myRole={myRole}
                  startValue={params.value as string}
                  submitChange={(value: string) => {
                    props.setUserField(
                      params.row.email,
                      'name',
                      value,
                      enqueueSnackbar
                    )
                    setEditRowsModel({
                      [params.id]: { name: { mode: GridCellModes.View } },
                    })
                  }}
                />
              ),
            },
            {
              field: 'lastName',
              headerName: 'Last Name',
              width: 200,
              editable: true,
              renderCell: (params: GridRenderCellParams) => (
                <EditableCellComponent
                  value={params.value as string}
                  myRole={myRole}
                  onEditClick={() => {
                    let model: { [key: string]: any } = {}
                    model[params.id] = {
                      lastName: { value: params.value },
                    }
                    setEditRowsModel(model)
                    setEditRowsModel({
                      [params.id]: { lastName: { mode: GridCellModes.Edit } },
                    })
                  }}
                />
              ),
              renderEditCell: (params: GridRenderEditCellParams) => (
                <EditableCellEditComponent
                  myRole={myRole}
                  startValue={params.value as string}
                  submitChange={(value: string) => {
                    props.setUserField(
                      params.row.email,
                      'lastName',
                      value,
                      enqueueSnackbar
                    )
                    setEditRowsModel({})
                    setEditRowsModel({
                      [params.id]: { lastName: { mode: GridCellModes.View } },
                    })
                  }}
                />
              ),
            },
            {
              field: 'phone',
              headerName: 'Phone',
              width: 200,
              editable: true,
              renderCell: (params: GridRenderCellParams) => (
                <EditableCellComponent
                  value={params.value as string}
                  myRole={myRole}
                  onEditClick={() => {
                    let model: { [key: string]: any } = {}
                    model[params.id] = {
                      phone: { value: params.value },
                    }
                    setEditRowsModel(model)
                    setEditRowsModel({
                      [params.id]: { phone: { mode: GridCellModes.Edit } },
                    })
                  }}
                />
              ),
              renderEditCell: (params: GridRenderEditCellParams) => (
                <EditableCellEditComponent
                  myRole={myRole}
                  isNumber
                  startValue={params.value as string}
                  submitChange={(value: string) => {
                    props.setUserField(
                      params.row.email,
                      'phone',
                      value,
                      enqueueSnackbar
                    )
                    setEditRowsModel({})
                    setEditRowsModel({
                      [params.id]: { phone: { mode: GridCellModes.View } },
                    })
                  }}
                />
              ),
            },
            {
              field: 'role',
              headerName: 'Role',
              width: 200,
              editable: true,
              renderCell: (params: GridRenderCellParams) => (
                <RoleCellComponent
                  role={params.value as RoleType}
                  onEditClick={() => {
                    let model: { [key: string]: any } = {}
                    model[params.id] = {
                      role: { value: params.value },
                    }
                    setEditRowsModel(model)
                    setEditRowsModel({
                      [params.id]: { role: { mode: GridCellModes.Edit } },
                    })
                  }}
                />
              ),
              renderEditCell: (params: GridRenderEditCellParams) => (
                <RoleCellEditComponent
                  myRole={myRole}
                  userRole={params.value as RoleType}
                  submitChange={(role: RoleType) => {
                    props.setUserRole(params.row.email, role, enqueueSnackbar)
                    setEditRowsModel({})
                    setEditRowsModel({
                      [params.id]: { role: { mode: GridCellModes.View } },
                    })
                  }}
                />
              ),
            },
          ]

          if (props.user && props.user.role === 'admin') {
            columns.push(
              {
                field: 'offerMarkups',
                headerName: 'Offer Markups',
                width: 200,
                renderCell: (params: GridRenderCellParams) => (
                  <Flex
                    centerY
                    sx={{ width: 1, justifyContent: 'space-between', gap: 2 }}
                  >
                    <Typography
                      sx={{
                        maxWidth: 0.7,
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                    >
                      {params.value}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        setOfferMarkupsEmail(params.row.email)
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Flex>
                ),
              },
              {
                field: 'ghlUrl',
                headerName: 'GHL URl',
                width: 200,
                editable: true,
                renderCell: (params: GridRenderCellParams) => (
                  <EditableCellComponent
                    value={params.value as string}
                    myRole={myRole}
                    onEditClick={() => {
                      let model: { [key: string]: any } = {}
                      model[params.id] = {
                        ghlUrl: { value: params.value },
                      }
                      setEditRowsModel(model)
                      setEditRowsModel({
                        [params.id]: { ghlUrl: { mode: GridCellModes.Edit } },
                      })
                    }}
                  />
                ),
                renderEditCell: (params: GridRenderEditCellParams) => (
                  <EditableCellEditComponent
                    myRole={myRole}
                    startValue={params.value as string}
                    submitChange={(value: string) => {
                      props.setUserField(
                        params.row.email,
                        'ghlUrl',
                        value,
                        enqueueSnackbar
                      )
                      setEditRowsModel({})
                      setEditRowsModel({
                        [params.id]: { ghlUrl: { mode: GridCellModes.View } },
                      })
                    }}
                  />
                ),
              },
              { field: 'refEmail', headerName: 'refEmail', width: 200 },
              {
                field: 'options',
                headerName: 'Options',
                filterable: false,
                sortable: false,
                renderCell: (params: GridCellParams) => (
                  <Box
                    onClick={(e) => {
                      e.stopPropagation() // don't select this row after clicking
                    }}
                  >
                    {params.row.role === 'company' && (
                      <IconButton
                        edge="end"
                        onClick={() => {
                          setOptionsEmail(params.row.email)
                        }}
                      >
                        <SettingsIcon />
                      </IconButton>
                    )}
                  </Box>
                ),
                width: 120,
              }
            )
          }

          columns.push({
            field: 'actions',
            headerName: 'Actions',
            filterable: false,
            sortable: false,
            renderCell: (params: GridCellParams) => (
              <Box
                onClick={(e) => {
                  e.stopPropagation() // don't select this row after clicking
                }}
              >
                <IconButton
                  title="Recover Password"
                  edge="end"
                  onClick={() => {
                    setRecoverPasswordEmail(params.row.email)
                  }}
                >
                  <PasswordIcon />
                </IconButton>

                {params.row.role === 'company' && (
                  <IconButton
                    sx={{ ml: 2 }}
                    title="Copy Company Invitation Link"
                    edge="end"
                    onClick={() => {
                      const invitationLink =
                        window.location.href
                          .split('//')[1]
                          .split('/profile')[0] +
                        '/?dir=signup/' +
                        encryptAES(params.row.email || '')
                      // console.log(params.row.email)

                      copyTextToClipboard(invitationLink, enqueueSnackbar)
                    }}
                  >
                    <LinkIcon />
                  </IconButton>
                )}
              </Box>
            ),
            width: 120,
          })

          return columns
        })()}
        // hideFooterPagination
        rows={props.usersInfo.map((userInfo) => ({
          id: userInfo.id,
          email: userInfo.email,
          name: userInfo.name,
          lastName: userInfo.lastName,
          phone: userInfo.phone,
          role: userInfo.role,
          offerMarkups: userInfo.offerMarkups.join(';'),
          refEmail: userInfo.refEmail,
          ghlUrl: userInfo.ghlUrl,
        }))}
        sortModel={props.usersInfoGridSortModel}
        onSortModelChange={(model) => {
          if (
            JSON.stringify(model) !==
            JSON.stringify(props.usersInfoGridSortModel)
          )
            props.setUsersInfoGridSortModel(model)
        }}
        filterModel={props.usersInfoGridFilterModel}
        onFilterModelChange={(model) => {
          if (
            JSON.stringify(model) !==
            JSON.stringify(props.usersInfoGridFilterModel)
          )
            props.setUsersInfoGridFilterModel(model)
        }}
        cellModesModel={editRowsModel}
        showCellVerticalBorder
      />

      <Dialog
        open={offerMarkups.length > 0}
        onClose={() => setOfferMarkupsEmail(undefined)}
      >
        <DialogTitle>Company {selectedCompany?.email} Markups</DialogTitle>
        <DialogContent>
          <OfferMarkup
            role={'admin'}
            title="Cabinet Markup"
            firstValue={offerMarkups[0] || 0}
            secondValue={offerMarkups[1] || 0}
            firstOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 0 ? newValue : v))
              )
            }
            secondOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 1 ? newValue : v))
              )
            }
          />

          <OfferMarkup
            role={'admin'}
            title="Slatwall Markup"
            firstValue={offerMarkups[2] || 0}
            secondValue={offerMarkups[3] || 0}
            firstOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 2 ? newValue : v))
              )
            }
            secondOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 3 ? newValue : v))
              )
            }
          />

          <OfferMarkup
            role={'admin'}
            title="Slatwall Accessories Markup"
            firstValue={offerMarkups[4] || 0}
            secondValue={offerMarkups[5] || 0}
            firstOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 4 ? newValue : v))
              )
            }
            secondOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 5 ? newValue : v))
              )
            }
          />

          <OfferMarkup
            role={'admin'}
            title="Overhead Storage Markup"
            firstValue={offerMarkups[6] || 0}
            secondValue={offerMarkups[7] || 0}
            firstOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 6 ? newValue : v))
              )
            }
            secondOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 7 ? newValue : v))
              )
            }
          />

          <OfferMarkup
            role={'admin'}
            title="Counter top Markup"
            firstValue={offerMarkups[8] || 0}
            secondValue={offerMarkups[9] || 0}
            firstOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 8 ? newValue : v))
              )
            }
            secondOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 9 ? newValue : v))
              )
            }
          />

          <OfferMarkup
            role={'admin'}
            title="Garage Shop Markup"
            firstValue={offerMarkups[10] || 0}
            secondValue={offerMarkups[11] || 0}
            firstOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 10 ? newValue : v))
              )
            }
            secondOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 11 ? newValue : v))
              )
            }
          />
          <OfferMarkup
            role={'admin'}
            title="Floor Coating Markup"
            firstValue={offerMarkups[12] || 0}
            secondValue={offerMarkups[13] || 0}
            firstOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 12 ? newValue : v))
              )
            }
            secondOnChange={(newValue) =>
              setOfferMarkups(
                offerMarkups.map((v, i) => (i === 13 ? newValue : v))
              )
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3 }}>
          <Button
            size="medium"
            color="primary"
            variant="contained"
            onClick={handleSaveClick}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <CompanyOptionsDialog
        open={!!optionsEmail}
        selectedCompany={selectedCompany}
        companyOptions={selectedCompanyOptions}
        allDistributorCompanies={props.configs.floorDistributorCompanies.strValues.split(
          ','
        )}
        fuelSurchargeFee={props.configs.fuelSurchargeFee}
        close={() => setOptionsEmail(undefined)}
        editCompanyOptions={(options) => {
          props.editCompanyOptions(
            optionsEmail || '',
            JSON.stringify(options),
            enqueueSnackbar
          )
        }}
      />

      <Dialog
        open={!!recoverPasswordEmail}
        onClose={() => setRecoverPasswordEmail('')}
      >
        <DialogTitle>Recover Password</DialogTitle>
        <DialogContent dividers>
          <Typography>User Email - {recoverPasswordEmail}</Typography>
          <Typography sx={{ my: 2 }}>
            Recovery Code - {actualRecoveryCode || 'none'}
          </Typography>
          {actualRecoveryCode && (
            <>
              <Button
                onClick={() => {
                  copyTextToClipboard(
                    actualRecoveryCode,
                    enqueueSnackbar,
                    'Code Copied'
                  )
                }}
                size="small"
                variant="contained"
              >
                Copy Code
              </Button>
              <Button
                onClick={() => {
                  const recoveryLink =
                    window.location.href.split('//')[1].split('/profile')[0] +
                    '/?dir=forgot/' +
                    actualRecoveryCode
                  copyTextToClipboard(
                    recoveryLink,
                    enqueueSnackbar,
                    'Recovery Link Copied'
                  )
                }}
                sx={{ ml: 2 }}
                size="small"
                variant="contained"
              >
                Copy Link
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Flex centerX fullWidth>
            <Button
              variant="contained"
              onClick={() =>
                props.generateRecoveryCode(
                  recoverPasswordEmail,
                  enqueueSnackbar
                )
              }
            >
              Generate Recovery Code
            </Button>
          </Flex>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const mapStateToProps = (state: StateType) => ({
  configs: state.appReducer.configs,
  usersInfo: state.appReducer.usersInfo,
  recoveryCodes: state.appReducer.recoveryCodes,
  user: state.appReducer.user,
  usersInfoGridVisibilityModel: state.appReducer.usersInfoGrid.visibilityModel,
  usersInfoGridSortModel: state.appReducer.usersInfoGrid.sortModel,
  usersInfoGridFilterModel: state.appReducer.usersInfoGrid.filterModel,
})

export default connect(mapStateToProps, {
  setUserRole,
  setUsersInfoGridVisibilityModel: appActions.setUsersInfoGridVisibilityModel,
  setUsersInfoGridSortModel: appActions.setUsersInfoGridSortModel,
  setUsersInfoGridFilterModel: appActions.setUsersInfoGridFilterModel,
  editCompanyOfferMarkups,
  editCompanyOptions,
  setUserField,
  generateRecoveryCode,
})(UsersInfoTab)
